import { Colors, StandardTooltip } from '@brandfolder/react';
import { Trans } from '@lingui/macro';
import React, { FunctionComponent, useState, useMemo, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

import { getAttachmentFaces, AttachmentFaceResponse } from '@api/v4/private/people_tagging/faces';

import { FaceOverlay } from './FaceOverlay';
import { BoundingBox } from './types';

const PeopleSection = styled.div``;

const SectionTitle = styled.span`
  display: block;
  color: #4a4a4a;
  font-size: 14px;
  font-weight: 700;
  padding-top: 15px;
  text-transform: uppercase;
  font-family: "Circular-Medium", "Arial", "Trebuchet MS", sans-serif;
  margin-bottom: 12px;
`;

const PeopleGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 4px;
`;

const PersonItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 80px;
`;

const PersonImage = styled.img`
  width: 64px;
  height: 64px;
  border-radius: 4px;
  object-fit: cover;
  margin-bottom: 6px;
  cursor: pointer;
`;

const PersonName = styled.span`
  font-size: 12px;
  color: #4a4a4a;
  text-align: center;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;
`;

const PersonContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

interface Person {
  id: string;
  name: string;
  imageUrl: string;
  boundingBox: BoundingBox;
}

export interface PeopleInAssetProps {
  attachmentKey?: string;
}

export const PeopleInAsset: FunctionComponent<PeopleInAssetProps> = ({ attachmentKey = undefined }) => {
  const [imageErrors, setImageErrors] = useState<Record<string, boolean>>({});
  const [people, setPeople] = useState<Person[]>([]);
  const [hoveredPerson, setHoveredPerson] = useState<string | null>(null);

  const handleImageError = (personId: string): void => {
    setImageErrors((prev) => ({
      ...prev,
      [personId]: true
    }));
  };

  const handlePersonHover = useCallback((personId: string): void => {
    setHoveredPerson(personId);
  }, []);

  const handlePersonLeave = useCallback((): void => {
    setHoveredPerson(null);
  }, []);

  useEffect(() => {
    const brandfolderKey = BFG?.resource?.key;
    if (!brandfolderKey || !attachmentKey) return;

    const loadFaces = async (): Promise<void> => {
      try {
        const faces: AttachmentFaceResponse[] = await getAttachmentFaces({
          attachmentKey,
          brandfolderKey
        });

        const mapped: Person[] = faces.map((f) => ({
          id: f.id,
          name: f.identity_id, // placeholder until name is resolved
          imageUrl: f.source_url,
          boundingBox: {
            height: f.bounding_box.height,
            left: f.bounding_box.left,
            top: f.bounding_box.top,
            width: f.bounding_box.width
          }
        }));

        setPeople(mapped);
      } catch {
        setPeople([]);
      }
    };

    void loadFaces();
  }, [attachmentKey]);

  const faceOverlaysPortal = useMemo(() => {
    const assetPreviewWrapper = document.getElementById('asset-preview-wrapper');
    if (!assetPreviewWrapper) return null;

    return createPortal(
      <>
        {people.map((person) => (
          <FaceOverlay
            key={person.id}
            boundingBox={person.boundingBox}
            isVisible={hoveredPerson === person.id}
            onHover={() => handlePersonHover(person.id)}
            onLeave={handlePersonLeave}
            personName={person.name}
          />
        ))}
      </>,
      assetPreviewWrapper
    );
  }, [hoveredPerson, handlePersonHover, handlePersonLeave, people]);

  return (
    <PeopleSection>
      <SectionTitle>
        <Trans>People in Asset</Trans>
      </SectionTitle>
      <PeopleGrid>
        {people.map((person) => (
          <PersonItem key={person.id}>
            <StandardTooltip
              color={Colors.None}
              id={`person-tooltip-${person.id}`}
              tooltip={person.name}
            >
              <PersonContent
                onMouseEnter={() => handlePersonHover(person.id)}
                onMouseLeave={handlePersonLeave}
              >
                <PersonImage
                  alt={`${person.name}`}
                  data-testid="person-thumbnail"
                  onError={() => handleImageError(person.id)}
                  src={imageErrors[person.id]
                    ? `${BFG?.icon_service_url || ''}/assets/types/person.svg`
                    : person.imageUrl}
                />
                <PersonName>{person.name}</PersonName>
              </PersonContent>
            </StandardTooltip>
          </PersonItem>
        ))}
      </PeopleGrid>
      {faceOverlaysPortal}
    </PeopleSection>
  );
};
